"use client"
import Link from "next/link"
import Image from "next/image"
import { useEffect, useState } from "react"
import { useParams } from "next/navigation"
import Icon from "@/components/ui/Icon"
import { jsonLdScript } from "@/lib/jsonld"
import { useFavourites } from "@/lib/hooks/useFavourites"

export default function SuburbDetailPage() {
  const params = useParams()
  const slug = params.slug as string
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const { favs, toggle: toggleFavDB } = useFavourites()

  useEffect(() => {
    fetch("/api/suburbs/" + slug)
      .then(r => r.json())
      .then(d => { setData(d.data ?? null); setLoading(false) })
      .catch(() => setLoading(false))
  }, [slug])

  function toggleFav(id: string, e: React.MouseEvent) { toggleFavDB(id, e) }

  if (loading) return <div style={{ minHeight: "60vh", display: "grid", placeItems: "center", color: "var(--muted)" }}>Loading...</div>
  if (!data) return (
    <div style={{ minHeight: "60vh", display: "grid", placeItems: "center" }}>
      <div style={{ textAlign: "center" }}>
        <h2 style={{ fontFamily: "Fraunces, serif", color: "var(--navy)" }}>Suburb not found</h2>
        <Link href="/suburbs" style={{ color: "var(--gold-deep)", marginTop: 12, display: "inline-block" }}>Back to all suburbs</Link>
      </div>
    </div>
  )

  const SITE = "https://zimestates.co.zw"
  const placeLd = {
    "@context": "https://schema.org",
    "@type": "Place",
    name: data.name + ", " + data.city + ", Zimbabwe",
    description: `Property listings, sold prices and market data for ${data.name}, ${data.city}. Browse verified homes, stands and commercial properties for sale and rent.`,
    address: {
      "@type": "PostalAddress",
      addressLocality: data.name,
      addressRegion: data.city,
      addressCountry: "ZW",
    },
    url: SITE + "/suburbs/" + slug,
    ...(data.activeListings > 0 ? { numberOfRooms: data.activeListings } : {}),
  }
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home",    item: SITE },
      { "@type": "ListItem", position: 2, name: "Suburbs", item: SITE + "/suburbs" },
      { "@type": "ListItem", position: 3, name: data.name + ", " + data.city, item: SITE + "/suburbs/" + slug },
    ],
  }

  return (
    <div style={{ background: "var(--cream)", minHeight: "100vh" }}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLdScript(placeLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLdScript(breadcrumbLd) }} />
      <div style={{ background: "var(--navy)", padding: "56px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Link href="/suburbs" style={{ color: "rgba(253,251,247,0.55)", fontSize: 13, textDecoration: "none", display: "inline-block", marginBottom: 20 }}>{"<"} All suburbs</Link>
          <h1 style={{ fontFamily: "Fraunces, serif", fontSize: 44, fontWeight: 700, color: "var(--cream)", marginBottom: 6 }}>
            {data.name}
          </h1>
          <div style={{ fontSize: 15, color: "rgba(253,251,247,0.7)" }}>{data.city}</div>
          <div style={{ display: "flex", gap: 28, marginTop: 32, flexWrap: "wrap" }}>
            {[
              { label: "Avg USD/m²", value: data.avgPriceSqm ? "$" + Math.round(data.avgPriceSqm) : "-" },
              { label: "Active listings", value: data.activeListings ?? 0 },
              { label: "12-month growth", value: data.growth12m != null ? (data.growth12m > 0 ? "+" : "") + data.growth12m + "%" : "-", color: data.growth12m > 0 ? "var(--green)" : data.growth12m < 0 ? "var(--red)" : undefined },
              { label: "90-day growth", value: data.growth90d != null ? (data.growth90d > 0 ? "+" : "") + data.growth90d + "%" : "-", color: data.growth90d > 0 ? "var(--green)" : data.growth90d < 0 ? "var(--red)" : undefined },
            ].map(s => (
              <div key={s.label}>
                <div style={{ fontFamily: "Fraunces, serif", fontSize: 30, fontWeight: 700, color: (s as any).color ?? "var(--gold)" }}>{s.value}</div>
                <div style={{ fontSize: 12, color: "rgba(253,251,247,0.5)", textTransform: "uppercase", fontWeight: 600, marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 32 }}>
            <iframe
              src={`https://www.google.com/maps/embed/v1/place?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(data.name + " " + data.city + " Zimbabwe")}`}
              style={{ width: "100%", height: 200, border: 0, borderRadius: 12, display: "block" }}
              title={data.name + " location map"}
              loading="lazy"
              allowFullScreen
            />
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "48px 24px" }}>
        {data.soldPrices?.length > 0 && (
          <div style={{ marginBottom: 48 }}>
            <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 24, fontWeight: 600, color: "var(--navy)", marginBottom: 4 }}>What properties have sold for in {data.name}</h2>
            <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 20 }}>Agent-reported sales data.</p>
            <div style={{ background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 14, overflow: "hidden" }}>
              <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" } as React.CSSProperties}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 560 }}>
                <thead>
                  <tr style={{ background: "var(--cream-warm)", borderBottom: "1px solid var(--line)" }}>
                    {["Address", "Beds", "Floor area", "Sold price", "Date", "Days on market"].map(h => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.soldPrices.map((sp: any, i: number) => (
                    <tr key={sp.id} style={{ borderBottom: "1px solid var(--line)", background: i % 2 === 0 ? "var(--cream)" : "rgba(253,251,247,0.5)" }}>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: "var(--ink)" }}>{sp.address}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: "var(--muted)" }}>{sp.beds ?? "-"}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: "var(--muted)" }}>{sp.floorArea ? sp.floorArea + "m²" : "-"}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 600, color: "var(--navy)" }}>${sp.soldPrice.toLocaleString()}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: "var(--muted)" }}>{new Date(sp.soldDate).toLocaleDateString()}</td>
                      <td style={{ padding: "10px 14px", fontSize: 13, color: "var(--muted)" }}>{sp.daysOnMarket ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        )}

        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
            <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 24, fontWeight: 600, color: "var(--navy)" }}>Live listings in {data.name}</h2>
            <Link href={"/listings?suburb=" + data.name} style={{ fontSize: 13, color: "var(--gold-deep)", fontWeight: 600, textDecoration: "none" }}>View all {">"}
            </Link>
          </div>
          {data.listings?.length === 0 ? (
            <div style={{ padding: "48px 24px", textAlign: "center", background: "var(--cream-warm)", borderRadius: 14, border: "1px solid var(--line)" }}>
              <p style={{ color: "var(--muted)", marginBottom: 16 }}>No live listings in {data.name} right now.</p>
              <Link href="/listings" style={{ color: "var(--gold-deep)", fontWeight: 600 }}>Browse all listings</Link>
            </div>
          ) : (
            <div className="listings-grid">
              {data.listings.map((l: any) => {
                const photo = l.photos?.[0]?.url
                const fallbackBg = l.priceType === "RENT" ? "linear-gradient(135deg,#3d8b5f,#1B2D54)" : "linear-gradient(135deg,#3a4f7a,#1B2D54)"
                return (
                  <Link key={l.id} href={"/listings/" + l.id} className="listing-card">
                    <div className="listing-img" style={{ background: photo ? undefined : fallbackBg }}>
                      {photo && <Image src={photo} alt={l.title} fill sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" style={{ objectFit: "cover" }} />}
                      {l.isFeatured
                        ? <span className="listing-tag gold">FEATURED</span>
                        : <span className="listing-tag" style={{ background: l.priceType === "RENT" ? "var(--green)" : "var(--navy)" }}>{l.priceType === "SALE" ? "FOR SALE" : "FOR RENT"}</span>
                      }
                      <div className="listing-actions">
                        <button className="listing-action-btn" onClick={e => toggleFav(l.id, e)} style={{ color: favs.includes(l.id) ? "var(--red)" : "inherit" }}>{favs.includes(l.id) ? "♥" : "♡"}</button>
                      </div>
                    </div>
                    <div className="listing-body">
                      <div className="listing-price">${l.price.toLocaleString()} <em>{l.priceType === "RENT" ? "/mo" : "USD"}</em></div>
                      <div className="listing-title">{l.title}</div>
                      <div className="listing-loc">{l.suburb}, {l.city}</div>
                      <div className="listing-meta">
                        {l.beds > 0 && <span>{l.beds} beds</span>}
                        {l.baths > 0 && <span>{l.baths} bath</span>}
                        {l.floorArea > 0 && <span>{l.floorArea}m²</span>}
                      </div>
                      {l.trustScore && <div className="trust-score">Trust Score {l.trustScore.totalScore}/100</div>}
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
